import { Form, Formik } from "formik";
import { Button, Flex, InfoBox, Label, ModalV2, Text } from "@jsq/ds/core";
import { FormDatePicker, FormSelect } from "@jsq/ds/forms";
import { JsqAccessTypeEnum } from "~/graphql/supergraphTypes";
import { getMinSelectableDate } from "~/pages/UserAndRoles/utils/dateFormatter";
import { useFetchWorkspaceRoles } from "~/pages/UserAndRoles/WorkspacesPage/hooks";
import { SPACING } from "~/styles/variables";
import { CreateBulkArenaAccessParams, useBulkArenaAccess } from "./hooks";
import { validationSchema } from "./schema";

const ACCESS_TYPE_OPTIONS: { value: JsqAccessTypeEnum; label: string }[] = [
  { value: "named", label: "Named access" },
  { value: "unnamed", label: "Anonymous access" },
];

type CreateBulkArenaAccessModalProps = {
  isOpen: boolean;
  userId: string;
  onClose: () => void;
};

export const CreateBulkArenaAccessModal = ({
  isOpen,
  onClose,
  userId,
}: CreateBulkArenaAccessModalProps) => {
  const { createBulkArenaAccess } = useBulkArenaAccess({ userId });
  const { roles } = useFetchWorkspaceRoles();

  const initialValues: Omit<CreateBulkArenaAccessParams, "userId"> = {
    roleGroupName: roles[0]?.role ?? "",
    accessType: "named",
    endDate: null,
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={async (values, { resetForm, setSubmitting }) => {
        const success = await createBulkArenaAccess({
          roleGroupName: values.roleGroupName,
          accessType: values.accessType,
          endDate: values.endDate ? new Date(values.endDate).toISOString() : null,
        });
        setSubmitting(false);

        if (success) {
          resetForm();
          onClose();
        }
      }}
    >
      {({ isSubmitting, resetForm }) => {
        const handleClose = () => {
          resetForm();
          onClose();
        };

        return (
          <ModalV2
            show={isOpen}
            onHide={handleClose}
            title="Enable access to all client workspaces"
            description="Grant a workspace role across all FA-managed workspaces."
            size="medium"
            data-testid="bulk-arena-access-modal"
          >
            <Form>
              <ModalV2.Body>
                <Flex flexDirection="column" gap={SPACING.MEDIUM}>
                  <InfoBox variant="info" title="Super-user permission">
                    Grants the selected workspace role across every FA-managed client workspace.
                    Direct workspace assignments can still grant enhanced or time-bound access
                    to specific workspaces.
                  </InfoBox>
                  <FormSelect
                    name="roleGroupName"
                    label="Role"
                    placeholder="Select a role"
                    full
                    inModal
                    hideSelectAll
                    hideSelectNone
                    hasLeftPadding={false}
                    matchFromStart={false}
                    data-testid="bulk-arena-access-role-select"
                  >
                    <FormSelect.Section>
                      {roles.map((r) => (
                        <FormSelect.Option key={r?.role} value={r?.role}>
                          {r?.role}
                        </FormSelect.Option>
                      ))}
                    </FormSelect.Section>
                  </FormSelect>

                  <FormSelect
                    name="accessType"
                    label="Access type"
                    placeholder="Select access type..."
                    full
                    inModal
                    hideSelectAll
                    hideSelectNone
                    hasLeftPadding={false}
                    data-testid="bulk-arena-access-type-select"
                  >
                    <FormSelect.Section>
                      {ACCESS_TYPE_OPTIONS.map((option) => (
                        <FormSelect.Option key={option.value} value={option.value}>
                          {option.label}
                        </FormSelect.Option>
                      ))}
                    </FormSelect.Section>
                  </FormSelect>

                  <Flex flexDirection="column" data-testid="bulk-arena-access-expiration-date">
                    <Label>Expiration date</Label>
                    <FormDatePicker
                      name="endDate"
                      hasIcon
                      placeholderText="MM/DD/YYYY"
                      dateFormat="MM/dd/yyyy"
                      isFull
                      minDate={getMinSelectableDate()}
                    />
                    <Text variant="secondary-sm">Optional</Text>
                  </Flex>
                </Flex>
              </ModalV2.Body>
              <ModalV2.Footer>
                <Flex flexDirection="row" justifyContent="flex-end" gap={SPACING.SMALL}>
                  <Button variant="neutral" type="button" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    isLoading={isSubmitting}
                    disabled={isSubmitting}
                  >
                    Enable
                  </Button>
                </Flex>
              </ModalV2.Footer>
            </Form>
          </ModalV2>
        );
      }}
    </Formik>
  );
};
